/**
 * Verbindung zum DUELINE-Multiplayerserver.
 *
 * Diese Klasse kapselt alles Netzwerkspezifische an genau einer Stelle:
 * - Verbindungsaufbau
 * - Empfang des Serverzustands
 * - Latenzmessung (Ping)
 *
 * Der Client hat KEINE eigene Spiellogik. Er zeigt nur an, was der Server sagt
 * (Masterprompt Abschnitt 28).
 */

import { Client, Predict, type InputHandle, type Reconciler, type Room } from '@colyseus/sdk';
import { ladeZugangscode } from './zugangscode.js';
import {
  ClientMessage,
  MATCH_ROOM_NAME,
  PROTOCOL_VERSION,
  RECONNECT_SECONDS,
  ServerMessage,
  type ActionRejectedPayload,
  type CastSkillPayload,
  type DevActionPayload,
  type DevActionTypeValue,
  type MatchOutcome,
  type MatchPhase,
  type MatchState,
  type MoveInputData,
  type PingPayload,
  type PlayerSide,
  type PlayerState,
  type MatchEndedPayload,
  type MatchStatsPayload,
  type PongPayload,
  type RejectReasonType,
  type SelectTargetPayload,
  type SendWavePayload,
  type TierUpgradedPayload,
  type Vec2,
  type WaveSentPayload,
  type WelcomePayload,
  type ItemPayload,
  type LobbySignal,
  type LobbySignalPayload,
  type SpendSkillPointPayload,
  type HeroChoicePayload,
  createMatchCode,
  isValidMatchCode,
  normalizeMatchCode,
} from '@dueline/shared';
import {
  DEFAULT_HERO_ID,
  PORTAL_MAX_HP,
  UNIT_SEPARATION_PUSH_SPEED,
  UNIT_SEPARATION_RADIUS,
} from '@dueline/game-data';
import {
  applyHeroMovement,
  heroDarfLaufen,
  type SanctuaryZustand,
  heroSendetEingabe,
  istWarteraum,
  sumItemEffects,
  type FrontAnteil,
} from '@dueline/game-rules';
import type { MoveVector } from '../input/InputController.js';
import { bildschirmZuSpiel, spielZuBildschirm } from '../input/bildschirmRichtung.js';
import { addToHistory, type WaveDraft } from '../ui/composerModel.js';
import { countCreepsNearPortal } from '../ui/enemyViewModel.js';

/** Zustand der Verbindung, wie ihn die Benutzeroberflaeche sieht. */
/**
 * Wie man in ein Match kommt (Abschnitt 88).
 *
 * quick = beliebiger Gegner, create = privates Match eroeffnen,
 * join = privatem Match mit Code beitreten.
 */
export type JoinMode = 'quick' | 'create' | 'join' | 'bot';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

/** Momentaufnahme fuer die Anzeige. Wird bewusst nur mehrmals pro Sekunde erneuert. */
/** Ein Item im Besitz des eigenen Heroes - nur zum Anzeigen. */
export interface OwnedItemView {
  itemId: string;
  stage: number;
  investedGold: number;
  /** Solange groesser 0, ist das Item noch unterwegs. */
  deliverySecondsRemaining: number;
}

export interface MatchSnapshot {
  status: ConnectionStatus;
  /** Verstaendliche Fehlermeldung, falls etwas schiefgegangen ist. */
  errorMessage: string | null;
  matchId: string;
  /** Beitrittscode eines privaten Matches. Leer bei einem Schnellmatch. */
  matchCode: string;
  /** Eigene Seite, sobald der Server sie zugewiesen hat. */
  side: PlayerSide | null;
  phase: string;
  /** Restzeit der laufenden Phase in Sekunden. 0 ausserhalb von Vorbereitung und Countdown. */
  phaseSecondsRemaining: number;
  tick: number;
  elapsedSeconds: number;
  tickRate: number;
  playerCount: number;
  /** Anzahl der Creeps in der EIGENEN Lane. */
  creepCount: number;

  // --- Eigener Hero und eigenes Portal ---
  heroHp: number;
  heroMaxHp: number;
  heroAlive: boolean;
  /** Verbleibende Sekunden bis zum Respawn. 0, wenn der Hero lebt. */
  respawnInSeconds: number;
  portalHp: number;
  portalMaxHp: number;

  // --- Wirtschaft ---
  gold: number;
  income: number;
  tier: number;
  /** Restliche Wartezeit, bis wieder eine Welle gesendet werden darf. */
  waveCooldown: number;
  /** Creeps, die schon auf die gegnerische Lane zulaufen. */
  enemyLaneCreeps: number;
  /** Nimmt der Server Dev-Aktionen an? In Produktion immer false. */
  devMode: boolean;
  /**
   * Restliche Transferzeit jeder eigenen Welle, die noch unterwegs ist - die
   * naechste zuerst. Mehrere Wellen gleichzeitig sind erlaubt (Abschnitt 73).
   */
  wavesInTransit: number[];
  /**
   * Die letzten drei vom Server ANGENOMMENEN Wellen, die neueste zuerst
   * (Abschnitt 74: Last Wave und History).
   */
  waveHistory: WaveDraft[];
  /** ID der zuletzt angenommenen Welle - damit der Composer weiss, wann er leeren darf. */
  lastSentWaveId: string | null;
  /** Letzte Rueckmeldung des Servers zu einem Wunsch, fuer einen kurzen Hinweis. */
  notice: MatchNotice | null;

  // --- Enemy View (Abschnitte 83, 84) ---
  /** Was man vom Gegner sehen darf. null, solange keiner da ist. */
  opponent: OpponentInfo | null;
  /** Gegnerische Creeps in der EIGENEN Lane kurz vor dem Portal. */
  ownCreepsNearPortal: number;
  /** Millisekunden seit dem letzten Treffer am eigenen Portal. null = noch nie. */
  msSincePortalDamage: number | null;

  /**
   * Die beiden Frontlinien auf der gemeinsamen Achse (Abschnitt 13).
   *
   * 0 ist Blaus Anchor, 1 das von Orange - fuer beide Spieler dasselbe
   * Mass. Gebraucht fuer Watch Frontline und den Frontbalken.
   */
  frontlines: { blue: FrontAnteil; orange: FrontAnteil };
  /** Auf welcher Seite man selbst zuhause ist. */
  ownSide: PlayerSide;
  /** Letzter Treffer am eigenen Portal - fuer die deutliche Meldung (Abschnitt 84). */
  lastPortalDamage: PortalDamageEvent | null;

  // --- Level und Erfahrung (Abschnitte 45 bis 47) ---
  /** Aktuelles Hero-Level. */
  level: number;
  /** Gesamte Erfahrung im Match. */
  xp: number;
  /** Freie Skillpunkte, die noch niemand ausgegeben hat. */
  skillPoints: number;
  /** Offene Talentwahl (Level 8). */
  talentPending: boolean;
  /** Offene Capstone-Wahl (Level 10). */
  capstonePending: boolean;
  /** Ausbaustufe je Grundskill-Slot. */
  skillUpgrades: Record<number, number>;
  /** Gewaehltes Talent. Leer = noch keins. */
  talentId: string;
  /** Gewaehlter Capstone. Leer = noch keiner. */
  capstoneId: string;

  // --- Items (Abschnitt 66) ---
  /** Items in den Slots, in der Reihenfolge des Kaufs. */
  items: OwnedItemView[];
  healthPotions: number;
  manaPotions: number;
  /** Gemeinsame Abklingzeit der Consumables in Sekunden. */
  potionCooldown: number;

  // --- Verbindung (Abschnitt 30) ---
  /** Laeuft gerade ein Versuch, die Verbindung wiederherzustellen? */
  reconnecting: boolean;
  /** Restliche Zeit, in der man zurueckkommen kann - 0, wenn kein Versuch laeuft. */
  reconnectSecondsLeft: number;
  /** Wegen wem das Match gerade pausiert: 'self', 'opponent' oder null. */
  pausedFor: 'self' | 'opponent' | null;
  /** Restliche Pausenzeit in Sekunden. */
  pauseSecondsRemaining: number;

  // --- Hero-Skills ---
  heroId: string;
  mana: number;
  maxMana: number;
  /**
   * Ladestand der Passive des Arcanist, 0 bis 1.
   *
   * Bei 1 ist der naechste Skill kostenlos und staerker. Fuer alle anderen
   * Heroes bleibt der Wert auf 0 - die Anzeige wird dann gar nicht gezeigt.
   */
  overflowCharge: number;

  /**
   * Aufgestaute Stufen der Passive des Deadeye (Marked Prey).
   *
   * Bei allen anderen Heroes bleibt der Wert auf 0 - die Anzeige wird dann gar
   * nicht gezeigt.
   */
  markStacks: number;

  /** Aufgestauter Schwung der Passive des Shade (Momentum). */
  momentumStacks: number;

  /** So lange verlieren die Gegner den Shade noch aus den Augen. */
  hiddenFor: number;

  /**
   * Restliche Dauer eines Skills, den der Hero gerade wirkt (Barrage).
   *
   * Solange das laeuft, steht der Hero still. Die Anzeige sagt das auch, damit
   * niemand denkt, die Steuerung haenge.
   */
  channelSeconds: number;

  /**
   * Wo der Hero im Sanctuary-Ablauf steht (GAME_DESIGN Abschnitt 5f).
   *
   * Die Anzeige braucht beides: den Zustand fuer den Shop und die
   * Restzeit fuer den Fortschritt beim Hinein- und Hinausgehen.
   */
  sanctuary: string;
  sanctuarySeconds: number;

  /** Verbleibende Abklingzeit je Slot, Schluessel ist der Slot. */
  skillCooldowns: Record<number, number>;
  /** Gesamte Schadensreduktion, 0 bis 1. */
  damageReduction: number;
  /** Restlaufzeit des Ultimate in Sekunden. */
  ultimateActiveFor: number;
  /** Aktuell ausgewaehlter Gegner. */
  selectedCreepId: string | null;

  // --- Matchende ---
  /** null, solange das Match laeuft. */
  outcome: MatchOutcome | null;
  matchDurationSeconds: number;
  /** Warum das Match zu Ende ist. */
  matchEndReason: 'portal' | 'abandoned' | 'surrender' | null;
  /** Zahlen fuer den Ergebnisbildschirm (Abschnitt 92). */
  ownStats: MatchStatsPayload | null;
  opponentStats: MatchStatsPayload | null;
  /**
   * Traenke, die noch unterwegs sind (Produktentscheidung vom 05.10.2026).
   *
   * Je Art die Anzahl und die Restzeit des naechsten. Ohne diese Anzeige
   * kauft man einen Trank und es passiert scheinbar nichts.
   */
  healthPotionsIncoming: number;
  healthPotionNextSeconds: number;
  manaPotionsIncoming: number;
  manaPotionNextSeconds: number;
  /** Habe ich selbst zugestimmt, dass es losgehen kann? */
  lobbyReady: boolean;
  /** Habe ich ihn selbst gewaehlt, oder steht da nur die Vorbelegung? */
  heroChosen: boolean;
  /**
   * Zuletzt empfangene Kurznachricht aus dem Warteraum - oder null.
   *
   * Bewusst nur die LETZTE, nicht ein Verlauf: Es gibt keinen Chat zum
   * Nachlesen, sondern ein kurzes Zeichen "ich bin da". Die laufende
   * Nummer erkennt eine neue Nachricht, auch wenn es dieselbe ist.
   */
  lobbySignal: { id: number; signal: LobbySignal; vonMir: boolean } | null;
  /** Gemessene Rundlaufzeit in Millisekunden. */
  pingMs: number;
  /** Bilder pro Sekunde, gemessen im 3D-Bild. */
  fps: number;
  /** Dauer eines Bildes in Millisekunden. */
  frameTimeMs: number;
}

/**
 * Was man in der Enemy View vom Gegner sieht (Abschnitt 83).
 *
 * Bewusst NICHT enthalten: Gold, Income, Income Tick und die vorbereitete Welle.
 * Gold und Income bekommt dieser Client vom Server ohnehin nicht - und hier
 * wird auch nichts davon weitergereicht.
 */
export interface OpponentInfo {
  /** True, wenn der Gegner der Testgegner ist. */
  isBot: boolean;
  sessionId: string;
  side: PlayerSide | null;
  heroId: string;
  /** Echte HP-Quote 0 bis 1 - angezeigt wird nur ein gerundeter Wert. */
  hpRatio: number;
  alive: boolean;
  respawnInSeconds: number;
  mana: number;
  maxMana: number;
  tier: number;
  portalHp: number;
  /** Creeps, die gerade die Lane des Gegners angreifen - also die eigenen. */
  laneCreeps: number;
  /** Hat der Gegner einem neuen Match zugestimmt? */
  lobbyReady: boolean;
  /**
   * Hat der Gegner seinen Linebound schon gewaehlt?
   *
   * Nur OB, nicht WELCHEN (Produktentscheidung vom 05.10.2026): Wer den
   * Pick des anderen sieht und danach noch wechseln kann, betreibt
   * Counterpicking - und die Balance ist blind gemessen.
   */
  heroChosen: boolean;
  /** Ist der Gegner gerade verbunden? */
  connected: boolean;
}

/** Ein Treffer am eigenen Portal. */
export interface PortalDamageEvent {
  /** Laufende Nummer - so erkennt die Oberflaeche einen neuen Treffer. */
  id: number;
  amount: number;
}

/** Wie viele Traenke dieser Art sind gerade unterwegs? */
function zaehleUnterwegs(player: PlayerState | null | undefined, consumableId: string): number {
  if (!player) return 0;
  let anzahl = 0;
  for (const trank of player.pendingPotions) {
    if (trank.consumableId === consumableId) anzahl += 1;
  }
  return anzahl;
}

/** Restzeit des naechsten unterwegs befindlichen Trankes. 0 = keiner. */
function naechsteLieferung(player: PlayerState | null | undefined, consumableId: string): number {
  if (!player) return 0;
  let kleinste = 0;
  for (const trank of player.pendingPotions) {
    if (trank.consumableId !== consumableId) continue;
    if (kleinste === 0 || trank.deliverySecondsRemaining < kleinste) {
      kleinste = trank.deliverySecondsRemaining;
    }
  }
  return kleinste;
}

/** Phasen, in denen noch nicht gekaempft wird - dort gibt es kein Ergebnis. */
function isBeforeBattle(phase: string): boolean {
  return (
    phase === 'waiting' ||
    phase === 'lobby' ||
    phase === 'starting' ||
    phase === 'preparation' ||
    phase === 'countdown'
  );
}

/** Ordnet eine abgelehnte Nachricht der Stelle zu, um die es ging. */
function noticeActionFor(action: string): MatchNotice['action'] {
  switch (action) {
    case ClientMessage.UpgradeTier:
      return 'tier';

    case ClientMessage.BuyItem:
    case ClientMessage.SellItem:
    case ClientMessage.BuyConsumable:
    case ClientMessage.UseConsumable:
      return 'item';
    case ClientMessage.SpendSkillPoint:
    case ClientMessage.ChooseTalent:
    case ClientMessage.ChooseCapstone:
      return 'skill';
    default:
      return 'wave';
  }
}

/** Kurzer Hinweis in der Oberflaeche, z. B. "Nicht genug Gold". */
export interface MatchNotice {
  /** Laufende Nummer - so erkennt die Oberflaeche einen neuen Hinweis. */
  id: number;
  kind: 'info' | 'error';
  /** Worum es ging. */
  action: 'wave' | 'tier' | 'item' | 'skill';
  /** Grund einer Ablehnung, oder null bei einer Erfolgsmeldung. */
  reason: RejectReasonType | null;
  /** Bei einem Tier-Aufstieg: das neue Tier. */
  tier: number;
  /** Bei einer Erfolgsmeldung: bezahlter Preis und Income-Zuwachs. */
  cost: number;
  incomeGain: number;
}

const EMPTY_SNAPSHOT: MatchSnapshot = {
  status: 'idle',
  errorMessage: null,
  matchId: '',
  matchCode: '',
  side: null,
  phase: 'waiting',
  phaseSecondsRemaining: 0,
  tick: 0,
  elapsedSeconds: 0,
  tickRate: 0,
  playerCount: 0,
  creepCount: 0,
  heroHp: 0,
  heroMaxHp: 0,
  heroAlive: true,
  respawnInSeconds: 0,
  portalHp: 0,
  portalMaxHp: PORTAL_MAX_HP,
  gold: 0,
  income: 0,
  tier: 1,
  waveCooldown: 0,
  enemyLaneCreeps: 0,
  devMode: false,
  wavesInTransit: [],
  waveHistory: [],
  lastSentWaveId: null,
  notice: null,
  opponent: null,
  ownCreepsNearPortal: 0,
  msSincePortalDamage: null,
  frontlines: { blue: { anteil: 0, aktiv: false }, orange: { anteil: 0, aktiv: false } },
  ownSide: 'blue',
  lastPortalDamage: null,
  heroId: DEFAULT_HERO_ID,
  mana: 0,
  maxMana: 0,
  overflowCharge: 0,
  markStacks: 0,
  momentumStacks: 0,
  hiddenFor: 0,
  channelSeconds: 0,
  sanctuary: 'draussen',
  sanctuarySeconds: 0,
  level: 1,
  xp: 0,
  skillPoints: 0,
  talentPending: false,
  capstonePending: false,
  skillUpgrades: {},
  talentId: '',
  capstoneId: '',
  items: [],
  healthPotions: 0,
  manaPotions: 0,
  potionCooldown: 0,
  reconnecting: false,
  reconnectSecondsLeft: 0,
  pausedFor: null,
  pauseSecondsRemaining: 0,
  skillCooldowns: {},
  damageReduction: 0,
  ultimateActiveFor: 0,
  selectedCreepId: null,
  outcome: null,
  matchDurationSeconds: 0,
  matchEndReason: null,
  ownStats: null,
  opponentStats: null,
  healthPotionsIncoming: 0,
  healthPotionNextSeconds: 0,
  manaPotionsIncoming: 0,
  manaPotionNextSeconds: 0,
  lobbyReady: false,
  heroChosen: false,
  lobbySignal: null,
  pingMs: 0,
  fps: 0,
  frameTimeMs: 0,
};

/** Wie oft die Anzeige aktualisiert wird. Der Server tickt schneller als das noetig waere. */
const SNAPSHOT_INTERVAL_MS = 200;

/** Abstand zwischen zwei Latenzmessungen. */
const PING_INTERVAL_MS = 2000;

/**
 * Abstand zwischen zwei Rueckkehrversuchen nach einem Verbindungsabbruch.
 *
 * Kurz genug, dass man nach einem Funkloch sofort wieder drin ist, und lang
 * genug, dass der Server nicht mit Anfragen ueberschuettet wird.
 */
const RECONNECT_RETRY_MS = 1500;

/**
 * Schliesscodes, die ein bewusstes Verlassen bedeuten.
 *
 * 1000 ist ein sauber geschlossener Socket, 4000 meldet Colyseus bei einem
 * Verlassen mit Zustimmung ("consented").
 */
const LEAVE_CODES = new Set([1000, 4000]);

/**
 * Sollen wir nach einem Verbindungsende zurueckzukommen versuchen?
 *
 * Im Spiel aufgefallen (25.09.2026): Nach "Zurueck zum Menue" landete man
 * kurz im Hauptmenue und wurde sofort wieder ins Match gezogen - ein
 * bewusstes Verlassen wurde faelschlich als Abbruch gewertet.
 *
 * Aus dem WARTERAUM kommt man gar nicht zurueck (15.10.2026). Dort gibt der
 * Server den Platz sofort frei - ein Rueckkehrversuch koennte also nur
 * scheitern, und zwar eine Minute lang unter einer Meldung "Connection
 * lost". Besser gleich ins Menue und neu suchen.
 */
export function shouldReconnectAfterLeave(
  code: number | undefined,
  leavingOnPurpose: boolean,
  hasToken: boolean,
  imWarteraum: boolean,
): boolean {
  if (leavingOnPurpose || !hasToken || imWarteraum) return false;
  return code === undefined || !LEAVE_CODES.has(code);
}

/**
 * Platz im Browserspeicher fuer den Rueckkehrschluessel.
 *
 * Rueckmeldung aus dem Spiel (25.09.2026): Nach einem Neuladen kam man nicht
 * mehr ins laufende Match zurueck - der Schluessel lag nur im Arbeitsspeicher
 * und war mit der Seite weg. Genau das passiert am Handy staendig: Der
 * Browser entlaedt eine Seite im Hintergrund, um Speicher zu sparen.
 *
 * Bewusst der Sitzungsspeicher und nicht der dauerhafte: Er gehoert zu genau
 * diesem Tab. Sonst schnappt sich ein zweites Fenster desselben Browsers den
 * Platz des ersten - beim Testen mit zwei Fenstern sofort passiert.
 */
const RESUME_STORAGE_KEY = 'dueline.resume';

/** Was wir uns fuer eine Rueckkehr merken. */
interface ResumeData {
  token: string;
  /** Zeitpunkt des Abbruchs - danach laeuft die Reservierung ab. */
  savedAt: number;
}

/** Merkt sich den Schluessel ueber ein Neuladen hinweg. */
function speichereRueckkehr(token: string): void {
  try {
    const daten: ResumeData = { token, savedAt: Date.now() };
    sessionStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(daten));
  } catch {
    // Kein Speicher verfuegbar (privates Fenster) - dann eben ohne.
  }
}

/** Holt einen noch gueltigen Schluessel, falls vorhanden. */
function ladeRueckkehr(): string | null {
  try {
    const roh = sessionStorage.getItem(RESUME_STORAGE_KEY);
    if (!roh) return null;

    const daten = JSON.parse(roh) as ResumeData;
    const alterSekunden = (Date.now() - daten.savedAt) / 1000;
    // Abgelaufen: Der Server haelt den Platz nicht laenger frei.
    if (!daten.token || alterSekunden > RECONNECT_SECONDS) {
      sessionStorage.removeItem(RESUME_STORAGE_KEY);
      return null;
    }
    return daten.token;
  } catch {
    return null;
  }
}

/** Vergisst den Schluessel. */
function vergissRueckkehr(): void {
  try {
    sessionStorage.removeItem(RESUME_STORAGE_KEY);
  } catch {
    // Nichts zu tun.
  }
}

type Listener = (snapshot: MatchSnapshot) => void;

export class MatchConnection {
  private client: Client;
  private room: Room<unknown, MatchState> | null = null;
  private snapshot: MatchSnapshot = EMPTY_SNAPSHOT;
  /** Laufende Nummer der Kurznachrichten - siehe MatchSnapshot.lobbySignal. */
  private lobbySignalId = 0;
  private nextNoticeId = 1;
  /** Letzter bekannter Stand des eigenen Portals - um Treffer zu erkennen. */
  private lastPortalHp: number | null = null;
  private lastPortalDamageAtMs: number | null = null;
  private nextPortalDamageId = 1;

  /** Ankunftszeitpunkte (performance.now) aller eigenen Wellen, die noch unterwegs sind. */
  private waveArrivals: number[] = [];
  private listeners = new Set<Listener>();
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private snapshotTimer: ReturnType<typeof setInterval> | null = null;

  /** Vorhersage- und Glaettungsschicht. Existiert erst nach dem Verbinden. */
  private predict: Predict<MatchState> | null = null;
  /** Kanal, ueber den Bewegungsabsichten an den Server gehen. */
  private inputHandle: InputHandle<MoveInputData> | null = null;
  /** Vorhersage des eigenen Heroes samt Korrektur durch den Server. */
  private reconciler: Reconciler<PlayerState, MoveInputData> | null = null;
  /** Woher die Bewegungsrichtung kommt. Standard: keine Bewegung. */
  private inputSource: () => MoveVector = () => ({ x: 0, y: 0 });

  /**
   * Schluessel, mit dem sich derselbe Platz wieder einnehmen laesst.
   *
   * Er kommt vom Server und gilt nur fuer dieses eine Match. Ohne ihn waere
   * eine Rueckkehr ein ganz normaler Beitritt - also ein neuer Platz.
   */
  private reconnectionToken: string | null = null;
  /**
   * Wir haben das Match selbst verlassen.
   *
   * Im Spiel aufgefallen (25.09.2026): Nach "Zurueck zum Menue" landete man
   * kurz im Hauptmenue und wurde sofort wieder ins Match gezogen. Colyseus
   * meldet ein bewusstes Verlassen naemlich nicht mit Code 1000, sondern mit
   * 4000 - und alles ausser 1000 galt hier als Verbindungsabbruch.
   */
  private leavingOnPurpose = false;
  /** Laeuft gerade ein Rueckkehrversuch? */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Wann der Versuch begonnen hat - fuer die Restzeit in der Anzeige. */
  private reconnectStartedAt = 0;

  constructor(serverUrl: string) {
    this.client = new Client(serverUrl);
  }

  /** Aktueller Stand fuer die Anzeige. */
  getSnapshot(): MatchSnapshot {
    return this.snapshot;
  }

  /**
   * Der rohe Serverzustand.
   * Das 3D-Bild liest ihn direkt in jedem Einzelbild - deshalb laeuft er
   * absichtlich an React vorbei.
   */
  getState(): MatchState | null {
    return this.room?.state ?? null;
  }

  /** Meldet einen Zuhoerer an. Gibt eine Funktion zum Abmelden zurueck. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Meldet die aktuelle Bildrate aus dem 3D-Bild zurueck an die Anzeige. */
  reportFrameTiming(fps: number, frameTimeMs: number): void {
    this.snapshot = { ...this.snapshot, fps, frameTimeMs };
  }

  /**
   * Verbindet sich mit einem Match (Abschnitt 88).
   *
   * - **quick:** sucht einen beliebigen Gegner. Alle Schnellmatches teilen sich
   *   den leeren Code und werden deshalb zusammengelegt.
   * - **create:** legt ein privates Match mit neuem Code an.
   * - **join:** tritt einem privaten Match bei. Gibt es keines mit diesem Code,
   *   meldet der Server das - es wird ausdruecklich KEIN neues angelegt, sonst
   *   wartete man nach einem Tippfehler ewig auf einen Gegner, der nie kommt.
   */
  async connect(
    mode: JoinMode = 'quick',
    code = '',
    heroId = DEFAULT_HERO_ID,
    /** Nur fuer Bot-Matches: Wen der Testgegner spielen soll. Leer = Zufall. */
    botHeroId = '',
  ): Promise<void> {
    // Bei 'bot' steht in code die gewuenschte Staerke.
    if (this.snapshot.status === 'connecting' || this.snapshot.status === 'connected') return;
    this.update({ status: 'connecting', errorMessage: null });

    try {
      const room = await this.joinRoom(mode, code, heroId, botHeroId);
      this.room = room as unknown as Room<unknown, MatchState>;
      this.reconnectionToken = room.reconnectionToken ?? null;
      if (this.reconnectionToken) speichereRueckkehr(this.reconnectionToken);
      this.attachHandlers(room as unknown as Room<unknown, MatchState>);
      this.setupPrediction();
      this.update({ status: 'connected' });
      this.startTimers();
    } catch (error) {
      this.update({
        status: 'error',
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /**
   * Fuehrt den eigentlichen Beitritt aus - je nach gewuenschter Art.
   *
   * Bei JEDER Art reist der Zugangscode mit (Produktentscheidung vom
   * 04.10.2026). Der Server entscheidet in onAuth, ob er zaehlt - solange
   * keine Codeliste eingestellt ist, wird er schlicht ignoriert, und in der
   * Entwicklung aendert sich dadurch nichts.
   */
  private async joinRoom(
    mode: JoinMode,
    code: string,
    heroId: string,
    botHeroId = '',
  ): Promise<Room<MatchState>> {
    const accessCode = ladeZugangscode();

    if (mode === 'create') {
      const neuerCode = createMatchCode();
      return this.client.create<MatchState>(MATCH_ROOM_NAME, {
        code: neuerCode,
        heroId,
        accessCode,
      });
    }

    if (mode === 'bot') {
      // Eigener Code, damit niemand sonst in dieses Match stolpert.
      return this.client.create<MatchState>(MATCH_ROOM_NAME, {
        code: createMatchCode(),
        bot: code || 'normal',
        heroId,
        // Leer heisst: Der Server sucht selbst einen aus.
        botHero: botHeroId,
        accessCode,
      });
    }

    if (mode === 'join') {
      const gesucht = normalizeMatchCode(code);
      if (!isValidMatchCode(gesucht)) throw new Error('invalid_code');
      return this.client.join<MatchState>(MATCH_ROOM_NAME, {
        code: gesucht,
        heroId,
        accessCode,
      });
    }

    return this.client.joinOrCreate<MatchState>(MATCH_ROOM_NAME, {
      code: '',
      heroId,
      accessCode,
    });
  }

  /**
   * Verlaesst das laufende Match und kehrt in den Ausgangszustand zurueck.
   *
   * Anders als ein Neuladen der Seite: Die Verbindung bleibt bestehen, und man
   * landet wieder im Hauptmenue.
   */
  async leaveMatch(): Promise<void> {
    this.leavingOnPurpose = true;
    // Der Schluessel muss WEG sein, bevor das Verlassen gemeldet wird - sonst
    // versucht ein spaeter Aufruf noch, ihn zu benutzen.
    this.reconnectionToken = null;
    vergissRueckkehr();

    await this.room?.leave();
    this.room = null;
    this.predict = null;
    this.inputHandle = null;
    this.stopReconnecting();
    this.update({ ...EMPTY_SNAPSHOT, devMode: this.snapshot.devMode });
    this.leavingOnPurpose = false;
  }

  /**
   * Legt fest, woher die Bewegungsrichtung des Spielers kommt.
   * Wird einmalig von der Anwendung gesetzt.
   */
  setInputSource(source: () => MoveVector): void {
    this.inputSource = source;
  }

  /**
   * Treibt die Vorhersage an. Muss in JEDEM Einzelbild aufgerufen werden.
   *
   * So funktioniert es:
   * 1. `predict.tick` sagt, wie viele feste Simulationsschritte seit dem letzten
   *    Bild faellig sind (bei 20 Takten pro Sekunde meist 0 oder 1).
   * 2. Fuer jeden faelligen Schritt schicken wir die aktuelle Richtung an den
   *    Server UND rechnen sofort selbst aus, wo wir dadurch landen.
   * 3. Sobald die echte Serverposition eintrifft, wird zurueckgespult, die
   *    Wahrheit uebernommen und die noch unbestaetigten Eingaben neu gerechnet.
   *    Weicht das Ergebnis ab, gleitet die Figur sanft auf die richtige Position -
   *    ohne sichtbares Springen.
   *
   * Dadurch reagiert die Steuerung sofort, obwohl der Server das letzte Wort hat.
   */
  tickPrediction(nowMs: number): void {
    if (!this.predict || !this.inputHandle) return;

    /*
     * Waehrend einer Pause wird nichts vorhergesagt und nichts gesendet.
     *
     * Der Server rechnet ohnehin nicht weiter. Wuerde der Client weiter
     * vorhersagen, liefe der Hero im Bild davon und rutschte nach der Pause
     * zurueck (Produktentscheidung vom 25.09.2026: Es steht wirklich alles).
     */
    if (this.snapshot.pausedFor !== null) {
      // Die Uhr trotzdem weiterstellen, damit danach nicht alles auf einmal
      // nachgeholt wird.
      this.predict.tick(nowMs);
      return;
    }

    const steps = this.predict.tick(nowMs);
    if (steps <= 0) return;

    const eigener = this.getOwnPlayer();
    const move = movementIntent(
      eigener
        ? // ABSICHTLICH die lockere Frage: Im Raum des Sanctuary laeuft er
          // wirklich herum (Abschnitt 5f, 09.10.2026).
          heroSendetEingabe(eigener)
        : // Noch kein Zustand da: Die Eingabe geht durch, der Server
          // prueft sie ohnehin selbst.
          true,
      this.inputSource(),
      (eigener?.side as PlayerSide | undefined) ?? 'blue',
    );
    for (let i = 0; i < steps; i += 1) {
      this.inputHandle.data.moveX = move.x;
      this.inputHandle.data.moveY = move.y;
      this.inputHandle.send();
    }
  }

  /**
   * Die Position, die tatsaechlich gezeichnet werden soll.
   *
   * - Eigener Hero: die vorhergesagte, sofort reagierende Position.
   * - Gegnerischer Hero: die weich interpolierte Serverposition.
   * - Ohne Vorhersage (z. B. direkt nach dem Verbinden): der rohe Serverwert.
   */
  getRenderPosition(player: PlayerState): { x: number; y: number } {
    if (this.predict) {
      return {
        x: this.predict.value(player, 'x'),
        y: this.predict.value(player, 'y'),
      };
    }
    return { x: player.x, y: player.y };
  }

  /**
   * Bittet den Server, einen Skill zu wirken.
   *
   * Es wird bewusst NICHTS vorhergesagt: Ein Skill hat Kosten, Abklingzeiten
   * und Treffer - wuerde der Client das vorwegnehmen und der Server anders
   * entscheiden, muesste man den Effekt sichtbar zuruecknehmen. Bei der
   * Bewegung lohnt sich die Vorhersage, hier nicht.
   */
  castSkill(slot: number, aim: Vec2): void {
    const payload: CastSkillPayload = { slot, aimX: aim.x, aimY: aim.y };
    this.room?.send(ClientMessage.CastSkill, payload);
  }

  /**
   * Waehlt einen Gegner als bevorzugtes Ziel (Masterprompt Abschnitt 44).
   * Ein leerer Text hebt die Auswahl wieder auf.
   */
  selectTarget(creepId: string | null): void {
    const payload: SelectTargetPayload = { creepId: creepId ?? '' };
    this.room?.send(ClientMessage.SelectTarget, payload);
    this.update({ selectedCreepId: creepId });
  }

  /**
   * Bittet den Server, eine Welle zum Gegner zu schicken.
   *
   * Auch hier wird nichts vorweggenommen: Gold, Income und die Welle selbst
   * aendern sich erst, wenn der Server den Kauf bestaetigt hat.
   */
  /**
   * Gibt das laufende Match auf (Abschnitt 90).
   *
   * Anders als leaveMatch bleibt man im Raum: Man sieht sein Ergebnis und
   * kann ein neues Match anbieten.
   */
  surrender(): void {
    this.room?.send(ClientMessage.Surrender);
  }

  sendWave(plan: SendWavePayload): void {
    this.room?.send(ClientMessage.SendWave, plan);
  }

  /**
   * Stimmt einem neuen Match zu (Abschnitt 90: Rematch).
   * Gestartet wird erst, wenn BEIDE zugestimmt haben.
   */
  /**
   * Schaltet "Ich bin bereit" um - im Warteraum wie nach einem Match
   * (Produktentscheidung vom 05.10.2026).
   *
   * Nochmal gedrueckt nimmt die Zustimmung zurueck. Ob das noch erlaubt
   * ist, entscheidet der Server: Sobald der Countdown laeuft, nicht mehr.
   */
  /**
   * Waehlt den Linebound in der Lobby (Produktentscheidung vom 13.10.2026).
   *
   * Geprueft wird auf dem Server: Ausserhalb der Lobby wird die Nachricht
   * verworfen, und ein unbekannter Linebound ebenso.
   */
  selectHero(heroId: string): void {
    this.room?.send(ClientMessage.SelectHero, { heroId });
  }

  toggleLobbyReady(): void {
    this.room?.send(ClientMessage.LobbyReady);
  }

  /**
   * Schickt eine der festen Kurznachrichten.
   *
   * Uebertragen wird nur die Kennung, nie Text - es gibt hier gar kein
   * Eingabefeld. Der Server reicht sie weiter und speichert nichts.
   */
  sendLobbySignal(signal: LobbySignal): void {
    this.room?.send(ClientMessage.LobbySignal, { signal } satisfies LobbySignalPayload);
  }

  /** Kauft ein Item oder baut es aus (Abschnitt 66). */
  buyItem(itemId: string): void {
    const payload: ItemPayload = { itemId };
    this.room?.send(ClientMessage.BuyItem, payload);
  }

  /** Verkauft ein Item. Bringt 65 % des eingesetzten Goldes zurueck. */
  sellItem(itemId: string): void {
    const payload: ItemPayload = { itemId };
    this.room?.send(ClientMessage.SellItem, payload);
  }

  /** Kauft ein Consumable. Belegt keinen Slot. */
  buyConsumable(itemId: string): void {
    const payload: ItemPayload = { itemId };
    this.room?.send(ClientMessage.BuyConsumable, payload);
  }

  /** Trinkt ein Consumable. */
  useConsumable(itemId: string): void {
    const payload: ItemPayload = { itemId };
    this.room?.send(ClientMessage.UseConsumable, payload);
  }

  /** Steckt einen Skillpunkt in einen Grundskill (Abschnitt 46). */
  spendSkillPoint(slot: number): void {
    const payload: SpendSkillPointPayload = { slot };
    this.room?.send(ClientMessage.SpendSkillPoint, payload);
  }

  /** Waehlt das Talent auf Level 8. Endgueltig. */
  chooseTalent(choiceId: string): void {
    const payload: HeroChoicePayload = { choiceId };
    this.room?.send(ClientMessage.ChooseTalent, payload);
  }

  /** Waehlt den Capstone auf Level 10. Endgueltig. */
  chooseCapstone(choiceId: string): void {
    const payload: HeroChoicePayload = { choiceId };
    this.room?.send(ClientMessage.ChooseCapstone, payload);
  }

  /** Bittet den Server, das naechste Tier freizuschalten (Abschnitt 64). */
  upgradeTier(): void {
    this.room?.send(ClientMessage.UpgradeTier);
  }

  /** Dev-/Cheat-Aktion. Der Server fuehrt sie nur im Dev-Modus aus. */
  devAction(action: DevActionTypeValue): void {
    const payload: DevActionPayload = { action };
    this.room?.send(ClientMessage.DevAction, payload);
  }

  /**
   * Die Frontlinien beider Spieler, nach Seite sortiert.
   *
   * Der Zustand haelt sie je Spieler; die Anzeige denkt in Blau und
   * Orange. Die Umsortierung steht hier an einer Stelle, statt in jedem
   * Stueck Oberflaeche noch einmal.
   */
  private leseFrontlinien(): { blue: FrontAnteil; orange: FrontAnteil } {
    const leer = { anteil: 0, aktiv: false };
    const state = this.room?.state;
    if (!state?.players) return { blue: leer, orange: leer };

    let blue = leer;
    let orange = leer;
    for (const spieler of state.players.values()) {
      const front = { anteil: spieler.frontlineAt, aktiv: spieler.frontlineAktiv };
      if (spieler.side === 'blue') blue = front;
      else orange = front;
    }
    return { blue, orange };
  }

  /** Sitzungs-ID des Gegners, sobald einer da ist. */
  getOpponentSessionId(): string | null {
    const own = this.room?.sessionId;
    const players = this.room?.state?.players;
    if (!own || !players) return null;
    for (const sessionId of players.keys()) {
      if (sessionId !== own) return sessionId;
    }
    return null;
  }

  /** Sitzungs-ID des eigenen Spielers, sobald verbunden. */
  getOwnSessionId(): string | null {
    return this.room?.sessionId ?? null;
  }

  /**
   * Der eigene Spieler im Serverzustand, falls schon vorhanden.
   *
   * Direkt nach dem Beitritt ist der Zustand noch nicht vollstaendig
   * uebertragen - dann gibt es die Spielerliste schlicht noch nicht. Deshalb
   * wird hier jeder Schritt einzeln geprueft, statt einen fertigen Zustand
   * vorauszusetzen.
   */
  getOwnPlayer(): PlayerState | null {
    const sessionId = this.room?.sessionId;
    const players = this.room?.state?.players;
    if (!sessionId || !players) return null;
    return players.get(sessionId) ?? null;
  }

  /** Trennt die Verbindung sauber - wird beim Verlassen der Seite aufgerufen. */
  async disconnect(): Promise<void> {
    this.stopTimers();
    this.reconciler = null;
    this.predict = null;
    this.inputHandle = null;
    const room = this.room;
    this.room = null;
    if (room) {
      try {
        await room.leave(true);
      } catch {
        // Verbindung war bereits weg - nichts zu tun.
      }
    }
    this.update({ status: 'disconnected' });
  }

  // ------------------------------------------------------------------ intern

  /**
   * Richtet Vorhersage und Glaettung ein.
   *
   * Zwei unterschiedliche Aufgaben:
   *
   * - **Eigener Hero (`reconciler`)**: Wird sofort mitgerechnet, damit sich die
   *   Steuerung verzoegerungsfrei anfuehlt, und laufend gegen die Wahrheit des
   *   Servers korrigiert.
   * - **Alle Spieler (`attachAll`)**: Der Server schickt etwa 20 Zustaende pro
   *   Sekunde, gezeichnet wird mit bis zu 60 Bildern. Dazwischen wird weich
   *   interpoliert, damit fremde Figuren nicht ruckeln.
   */
  private setupPrediction(): void {
    const room = this.room;
    if (!room) return;

    this.predict = Predict.get(room);

    // Weiche Bewegung fuer alle Spieler. Der eigene Hero wird gleich darauf vom
    // Reconciler uebernommen - der hat dann Vorrang.
    this.predict.attachAll('players', { fields: ['x', 'y'], mode: 'lerp' });

    // Die Beschreibung der Eingabefelder kommt beim Verbindungsaufbau vom
    // Server. Dadurch gibt es garantiert nur eine gueltige Definition.
    this.inputHandle = room.input({ mode: 'reliable' }) as InputHandle<MoveInputData>;

    // Der eigene Spieler existiert im Zustand unter Umstaenden noch nicht.
    // Sobald er da ist, wird die Vorhersage daran gebunden.
    this.attachReconcilerWhenReady();
  }

  private attachReconcilerWhenReady(): void {
    if (this.reconciler || !this.predict || !this.inputHandle) return;

    const player = this.getOwnPlayer();
    if (!player) return;

    this.reconciler = this.predict.reconciler(player, {
      input: this.inputHandle,
      fields: ['x', 'y'],
      // Exakt dieselbe Regel, die auch der Server rechnet.
      step: (ctx, state, command) => {
        /*
         * Genau dieselbe Regel wie auf dem Server - inklusive Sanctuary.
         *
         * Geschickt wird die Eingabe trotzdem (siehe oben): Hier geht es
         * nur darum, dass die Figur im Bild nicht losläuft, waehrend sie
         * auf dem Server steht.
         */
        /*
         * heroDarfLAUFEN und nicht heroDarfHandeln: Im Raum des Sanctuary
         * darf er sich bewegen (09.10.2026). Haette der Client hier die
         * strengere Frage gestellt, stuende der Hero im Bild still, waehrend
         * er auf dem Server laeuft.
         */
        if (!heroDarfLaufen(player)) return;
        /*
         * Die Seite gehoert seit der gemeinsamen Lane dazu: Die
         * Bewegungsgrenze haengt daran. Wuerde der Client hier eine andere
         * Seite annehmen als der Server, liefe die Vorhersage an der
         * Grenze auseinander - und der Hero zuckte dort.
         */
        /*
         * Die Leash kommt aus dem Zustand, nicht aus einer eigenen
         * Rechnung: Der Client kennt die Frontline nicht, und zwei
         * Rechnungen fuer dieselbe Grenze liefen frueher oder spaeter
         * auseinander.
         */
        applyHeroMovement(
          state,
          command,
          player.moveSpeed,
          ctx.dt,
          (player.side as PlayerSide | undefined) ?? 'blue',
          player.leashX,
          /*
           * Im Raum gelten dessen Waende statt der Lane-Grenzen. Ohne das
           * sagte die Vorhersage "zurueck auf die Lane" und der Server
           * "bleib im Raum" - der Hero zuckte bei jedem Schritt.
           */
          player.sanctuary as SanctuaryZustand,
          /*
           * Dieselben Koerper wie auf dem Server (13.10.2026).
           *
           * Es MUSS hier mitgerechnet werden: Ohne die Kollision liefe die
           * Vorhersage durch die Creepreihe hindurch, und die naechste
           * Serverantwort risse den Hero zurueck.
           */
          {
            hindernisse: [...(this.room?.state?.creeps?.values() ?? [])],
            mindestabstand: UNIT_SEPARATION_RADIUS,
            entflechtung: UNIT_SEPARATION_PUSH_SPEED * ctx.dt,
          },
        );
      },
    });
  }

  private attachHandlers(room: Room<unknown, MatchState>): void {
    room.onMessage(ServerMessage.Welcome, (payload: WelcomePayload) => {
      if (payload.protocolVersion !== PROTOCOL_VERSION) {
        this.update({
          status: 'error',
          errorMessage: `Protokollversion passt nicht (Client ${PROTOCOL_VERSION}, Server ${payload.protocolVersion}).`,
        });
        return;
      }
      this.update({
        matchId: payload.matchId,
        side: payload.side,
        tickRate: payload.tickRate,
        devMode: payload.devMode === true,
      });
    });

    /*
     * Kurznachrichten des Warteraums.
     *
     * Sie landen nur im Schnappschuss, nicht im Spielzustand - eine
     * verpasste Nachricht ist weg. Fuer einen Warteraum ist das richtig,
     * und es muss nichts gespeichert werden.
     */
    room.onMessage(ServerMessage.LobbySignal, (payload: LobbySignalPayload) => {
      this.lobbySignalId += 1;
      this.update({
        lobbySignal: {
          id: this.lobbySignalId,
          signal: payload.signal,
          vonMir: payload.sessionId === room.sessionId,
        },
      });
    });

    room.onMessage(ServerMessage.Pong, (payload: PongPayload) => {
      const roundTrip = Math.round(performance.now() - payload.clientTime);
      this.update({ pingMs: roundTrip });
    });

    room.onMessage(ServerMessage.WaveSent, (payload: WaveSentPayload) => {
      this.waveArrivals.push(performance.now() + payload.arrivesInSeconds * 1000);
      this.update({
        wavesInTransit: this.readWavesInTransit(),
        waveHistory: addToHistory(this.snapshot.waveHistory, {
          front: payload.front,
          center: payload.center,
          back: payload.back,
        }),
        lastSentWaveId: payload.waveId,
        notice: {
          id: this.nextNoticeId++,
          kind: 'info',
          action: 'wave',
          reason: null,
          tier: 0,
          cost: payload.cost,
          incomeGain: payload.incomeGain,
        },
      });
    });

    /*
     * Abgelehnt (Abschnitt 40): Jede Ablehnung wird sichtbar gemeldet.
     *
     * Vorher verschwanden Ablehnungen aus dem Shop und dem Skilltree
     * stillschweigend - man druckte auf einen Knopf, und es passierte nichts.
     */
    room.onMessage(ServerMessage.ActionRejected, (payload: ActionRejectedPayload) => {
      this.update({
        notice: {
          id: this.nextNoticeId++,
          kind: 'error',
          action: noticeActionFor(payload.action),
          reason: payload.reason,
          tier: 0,
          cost: 0,
          incomeGain: 0,
        },
      });
    });

    room.onMessage(ServerMessage.TierUpgraded, (payload: TierUpgradedPayload) => {
      this.update({
        notice: {
          id: this.nextNoticeId++,
          kind: 'info',
          action: 'tier',
          reason: null,
          tier: payload.tier,
          cost: payload.cost,
          incomeGain: 0,
        },
      });
    });

    room.onMessage(ServerMessage.MatchEnded, (payload: MatchEndedPayload) => {
      this.update({
        outcome: payload.outcome,
        matchDurationSeconds: payload.durationSeconds,
        matchEndReason: payload.reason,
        ownStats: payload.own,
        opponentStats: payload.opponent,
      });
    });

    room.onError((code, message) => {
      this.update({ status: 'error', errorMessage: `${message ?? 'Serverfehler'} (${code})` });
    });

    room.onLeave((code) => {
      this.stopTimers();

      /*
       * Wer selbst gegangen ist, bleibt gegangen.
       *
       * Colyseus meldet das mit Code 4000 ("consented"), ein sauber
       * geschlossener Socket mit 1000. Nur bei allem anderen - Funkloch,
       * Bildschirmsperre, Netzwechsel - kommen wir zurueck (Abschnitt 30).
       */
      if (
        !shouldReconnectAfterLeave(
          code,
          this.leavingOnPurpose,
          this.reconnectionToken !== null,
          istWarteraum(this.snapshot.phase as MatchPhase),
        )
      ) {
        // Ohne Rueckkehr ist auch der Schluessel wertlos - sonst zoege ihn
        // das naechste Neuladen aus dem Speicher und versuchte es erneut.
        vergissRueckkehr();
        this.update({ status: 'disconnected', reconnecting: false, reconnectSecondsLeft: 0 });
        return;
      }

      this.startReconnecting();
    });
  }

  /**
   * Versucht, denselben Platz wieder einzunehmen (Abschnitt 30).
   *
   * Der Server haelt ihn eine Minute lang frei. Wir probieren es in kurzen
   * Abstaenden - ein Funkloch dauert selten laenger als ein paar Sekunden,
   * eine Bildschirmsperre schon.
   */
  private startReconnecting(): void {
    if (this.reconnectTimer) return;

    this.reconnectStartedAt = performance.now();
    this.update({
      status: 'disconnected',
      reconnecting: true,
      reconnectSecondsLeft: RECONNECT_SECONDS,
    });

    const versuch = async () => {
      const verstrichen = (performance.now() - this.reconnectStartedAt) / 1000;
      const rest = Math.max(0, RECONNECT_SECONDS - verstrichen);

      if (rest <= 0 || !this.reconnectionToken) {
        this.stopReconnecting();
        vergissRueckkehr();
        this.update({ status: 'disconnected' });
        return;
      }

      this.update({ reconnectSecondsLeft: Math.ceil(rest) });

      try {
        const room = await this.client.reconnect<MatchState>(this.reconnectionToken);
        this.stopReconnecting();
        this.room = room as unknown as Room<unknown, MatchState>;
        this.reconnectionToken = room.reconnectionToken ?? this.reconnectionToken;
        if (this.reconnectionToken) speichereRueckkehr(this.reconnectionToken);
        this.attachHandlers(room as unknown as Room<unknown, MatchState>);
        this.setupPrediction();
        this.update({ status: 'connected', reconnecting: false, reconnectSecondsLeft: 0 });
        this.startTimers();
      } catch {
        // Noch nicht geklappt - gleich noch einmal.
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = null;
          void versuch();
        }, RECONNECT_RETRY_MS);
      }
    };

    void versuch();
  }

  /**
   * Kappt die Verbindung absichtlich - nur im Entwicklungsmodus.
   *
   * Ein Verbindungsabbruch laesst sich sonst kaum nachstellen: Man muesste das
   * WLAN ausschalten oder den Server anhalten. Damit sich die Rueckkehr
   * pruefen laesst, gibt es hier einen Knopf dafuer. Im ausgelieferten Spiel
   * ist die Methode nicht enthalten (Abschnitt 96).
   */
  dropForTest(): void {
    if (!import.meta.env.DEV) return;

    const verbindung = (this.room as unknown as { connection?: { transport?: { ws?: WebSocket } } })
      ?.connection;
    /*
     * Code 4999, nicht 4000.
     *
     * 4000 bedeutet in Colyseus ausdruecklich "bewusst verlassen" - damit
     * wuerde der Server den Platz sofort freigeben. 4999 ist einfach ein
     * Abbruch, so wie ihn ein Funkloch erzeugt.
     */
    verbindung?.transport?.ws?.close(4999, 'dev-drop');
  }

  /**
   * Versucht nach einem Neuladen, in ein laufendes Match zurueckzukehren.
   *
   * Wird einmal beim Start gerufen. Liegt kein Schluessel vor oder ist er zu
   * alt, passiert nichts - dann kommt wie gewohnt das Hauptmenue.
   *
   * @returns true, wenn die Rueckkehr geklappt hat.
   */
  async resumeIfPossible(): Promise<boolean> {
    const token = ladeRueckkehr();
    if (!token) return false;

    this.update({ status: 'connecting' });

    try {
      const room = await this.client.reconnect<MatchState>(token);
      this.room = room as unknown as Room<unknown, MatchState>;
      this.reconnectionToken = room.reconnectionToken ?? token;
      speichereRueckkehr(this.reconnectionToken);
      this.attachHandlers(room as unknown as Room<unknown, MatchState>);
      this.setupPrediction();
      this.update({ status: 'connected', reconnecting: false, reconnectSecondsLeft: 0 });
      this.startTimers();
      return true;
    } catch {
      // Match vorbei, Platz weg oder Server neu gestartet - halb so wild.
      vergissRueckkehr();
      this.update({ ...EMPTY_SNAPSHOT, devMode: this.snapshot.devMode });
      return false;
    }
  }

  /** Beendet die Rueckkehrversuche. */
  private stopReconnecting(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.update({ reconnecting: false, reconnectSecondsLeft: 0 });
  }

  private startTimers(): void {
    this.stopTimers();

    this.pingTimer = setInterval(() => {
      const payload: PingPayload = { clientTime: performance.now() };
      this.room?.send(ClientMessage.Ping, payload);
    }, PING_INTERVAL_MS);

    this.snapshotTimer = setInterval(() => this.refreshFromState(), SNAPSHOT_INTERVAL_MS);
  }

  private stopTimers(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    if (this.snapshotTimer) clearInterval(this.snapshotTimer);
    this.pingTimer = null;
    this.snapshotTimer = null;
  }

  /** Liest den Serverzustand und aktualisiert die Anzeigewerte. */
  private refreshFromState(): void {
    const state = this.room?.state;
    if (!state) return;

    // Der eigene Spieler taucht erst kurz nach dem Verbinden im Zustand auf.
    // Sobald er da ist, wird die Vorhersage daran gebunden.
    this.attachReconcilerWhenReady();

    const ownPlayer = this.getOwnPlayer();
    this.trackPortalDamage(ownPlayer?.portalHp ?? null);
    const ownSessionId = this.room?.sessionId ?? '';

    this.update({
      opponent: this.readOpponent(),
      ownCreepsNearPortal: state.creeps
        ? countCreepsNearPortal(state.creeps.values(), ownSessionId)
        : 0,
      msSincePortalDamage:
        this.lastPortalDamageAtMs === null ? null : performance.now() - this.lastPortalDamageAtMs,
      frontlines: this.leseFrontlinien(),
      ownSide: (ownPlayer?.side as PlayerSide | undefined) ?? 'blue',
      matchId: state.matchId || this.snapshot.matchId,
      matchCode: state.matchCode,
      phase: state.phase,
      phaseSecondsRemaining: state.phaseSecondsRemaining ?? 0,
      tick: state.tick,
      elapsedSeconds: state.elapsedSeconds,
      tickRate: state.tickRate || this.snapshot.tickRate,
      playerCount: state.players?.size ?? 0,
      creepCount: this.countOwnLaneCreeps(),
      side: (ownPlayer?.side as PlayerSide | undefined) ?? this.snapshot.side,
      heroHp: ownPlayer?.hp ?? 0,
      heroMaxHp: ownPlayer?.maxHp ?? 0,
      heroAlive: ownPlayer?.alive ?? true,
      respawnInSeconds: ownPlayer?.respawnInSeconds ?? 0,
      portalHp: ownPlayer?.portalHp ?? 0,
      wavesInTransit: this.readWavesInTransit(),
      // Sobald ein NEUES Match beginnt, verschwindet die Ergebnisanzeige von
      // selbst. Geprueft wird dabei auf die Phasen vor dem Kampf - nicht auf
      // "nicht mehr beendet":
      // Die Nachricht ueber das Matchende und die Zustandsaenderung treffen
      // getrennt ein. Kommt die Nachricht zuerst, stuende die Phase noch auf
      // "early" - und die gerade erhaltene Anzeige waere sofort wieder weg.
      outcome: isBeforeBattle(state.phase) ? null : this.snapshot.outcome,
      healthPotionsIncoming: zaehleUnterwegs(ownPlayer, 'health_potion'),
      healthPotionNextSeconds: naechsteLieferung(ownPlayer, 'health_potion'),
      manaPotionsIncoming: zaehleUnterwegs(ownPlayer, 'mana_potion'),
      manaPotionNextSeconds: naechsteLieferung(ownPlayer, 'mana_potion'),
      lobbyReady: ownPlayer?.lobbyReady ?? false,
      heroChosen: ownPlayer?.heroChosen ?? false,
      // Pause wegen Verbindungsabbruch (Abschnitt 30).
      pausedFor: state.pausedForSessionId
        ? state.pausedForSessionId === this.room?.sessionId
          ? 'self'
          : 'opponent'
        : null,
      pauseSecondsRemaining: state.pauseSecondsRemaining,
      gold: ownPlayer?.gold ?? 0,
      /*
       * Angezeigt wird der Income, den man WIRKLICH bekommt - inklusive
       * Trade Seal (Produktentscheidung vom 27.09.2026). Im Zustand steht
       * der Grundwert; der Zuschlag kommt erst bei der Auszahlung dazu, und
       * ohne ihn hier waere die Anzeige schlicht falsch.
       */
      income: Math.round(
        (ownPlayer?.income ?? 0) *
          (1 +
            sumItemEffects(
              (ownPlayer?.items ?? []).map((item) => ({
                itemId: item.itemId,
                stage: item.stage,
                investedGold: item.investedGold,
                deliverySecondsRemaining: item.deliverySecondsRemaining,
              })),
            ).incomeRatio),
      ),
      tier: ownPlayer?.tier ?? 1,
      waveCooldown: ownPlayer?.waveCooldown ?? 0,
      // Creeps, die auf den GEGNER zulaufen - also in seiner Lane stehen.
      enemyLaneCreeps: this.countEnemyLaneCreeps(),
      heroId: ownPlayer?.heroId || this.snapshot.heroId,
      mana: ownPlayer?.mana ?? 0,
      maxMana: ownPlayer?.maxMana ?? 0,
      overflowCharge: ownPlayer?.overflowCharge ?? 0,
      markStacks: ownPlayer?.markStacks ?? 0,
      momentumStacks: ownPlayer?.momentumStacks ?? 0,
      hiddenFor: ownPlayer?.hiddenFor ?? 0,
      sanctuary: ownPlayer?.sanctuary ?? 'draussen',
      sanctuarySeconds: ownPlayer?.sanctuarySeconds ?? 0,
      channelSeconds: ownPlayer?.channelSeconds ?? 0,
      level: ownPlayer?.level ?? 1,
      xp: ownPlayer?.xp ?? 0,
      skillPoints: ownPlayer?.skillPoints ?? 0,
      talentPending: ownPlayer?.talentPending ?? false,
      capstonePending: ownPlayer?.capstonePending ?? false,
      skillUpgrades: {
        1: ownPlayer?.skillUpgrades?.slot1 ?? 0,
        2: ownPlayer?.skillUpgrades?.slot2 ?? 0,
        3: ownPlayer?.skillUpgrades?.slot3 ?? 0,
      },
      talentId: ownPlayer?.talentId ?? '',
      capstoneId: ownPlayer?.capstoneId ?? '',
      items: (ownPlayer?.items ?? []).map((item) => ({
        itemId: item.itemId,
        stage: item.stage,
        investedGold: item.investedGold,
        deliverySecondsRemaining: item.deliverySecondsRemaining,
      })),
      healthPotions: ownPlayer?.healthPotions ?? 0,
      manaPotions: ownPlayer?.manaPotions ?? 0,
      potionCooldown: ownPlayer?.potionCooldown ?? 0,
      skillCooldowns: this.readSkillCooldowns(ownPlayer),
      damageReduction: ownPlayer?.damageReduction ?? 0,
      ultimateActiveFor: ownPlayer?.ultimateActiveFor ?? 0,
    });
  }

  /**
   * Erkennt Treffer am eigenen Portal (Abschnitt 84: Portalschaden klar melden).
   * Der Server meldet Treffer nicht einzeln - ein sinkender Stand genuegt.
   */
  private trackPortalDamage(portalHp: number | null): void {
    if (portalHp === null) return;
    const vorher = this.lastPortalHp;
    this.lastPortalHp = portalHp;
    if (vorher === null || portalHp >= vorher) return;

    this.lastPortalDamageAtMs = performance.now();
    this.update({ lastPortalDamage: { id: this.nextPortalDamageId++, amount: vorher - portalHp } });
  }

  /** Was man vom Gegner sehen darf (Abschnitt 83) - ohne Gold und Income. */
  private readOpponent(): OpponentInfo | null {
    const sessionId = this.getOpponentSessionId();
    const player = sessionId ? this.room?.state?.players?.get(sessionId) : null;
    if (!sessionId || !player) return null;

    let laneCreeps = 0;
    for (const creep of this.room?.state?.creeps?.values() ?? []) {
      if (creep.laneOwner === sessionId && creep.hp > 0) laneCreeps += 1;
    }

    return {
      sessionId,
      isBot: player.isBot ?? false,
      side: (player.side as PlayerSide | undefined) ?? null,
      heroId: player.heroId,
      hpRatio: player.maxHp > 0 ? player.hp / player.maxHp : 0,
      alive: player.alive,
      respawnInSeconds: player.respawnInSeconds,
      mana: player.mana,
      maxMana: player.maxMana,
      tier: player.tier,
      portalHp: player.portalHp,
      laneCreeps,
      lobbyReady: player.lobbyReady,
      heroChosen: player.heroChosen,
      connected: player.connected,
    };
  }

  /** Restliche Transferzeiten aller eigenen Wellen, in Sekunden, die naechste zuerst. */
  private readWavesInTransit(): number[] {
    const jetzt = performance.now();
    this.waveArrivals = this.waveArrivals.filter((ankunft) => ankunft > jetzt);
    return this.waveArrivals.map((ankunft) => (ankunft - jetzt) / 1000).sort((a, b) => a - b);
  }

  /** Liest die Abklingzeiten der Skills aus dem Serverzustand. */
  private readSkillCooldowns(player: PlayerState | null | undefined): Record<number, number> {
    const cooldowns: Record<number, number> = {};
    if (!player?.skills) return cooldowns;
    for (const skill of player.skills) {
      cooldowns[skill.slot] = skill.cooldownRemaining;
    }
    return cooldowns;
  }

  /**
   * Creeps in der Lane des Gegners.
   *
   * Der Composer braucht die Zahl: Mehr als MAX_SENT_CREEPS_PER_LANE nimmt der
   * Server nicht an, und das soll man vorher sehen.
   */
  private countEnemyLaneCreeps(): number {
    const gegner = this.getOpponentSessionId();
    if (!gegner) return 0;

    /*
     * Die Zahl kommt vom Server - selbst zaehlen waere falsch.
     *
     * Er rechnet die Creeps mit, die gerade erst losgeschickt wurden und noch
     * nicht erschienen sind. Wer selbst zaehlt, sieht sie nicht und haelt die
     * Lane faelschlich fuer leer (25.09.2026).
     */
    return this.room?.state?.players?.get(gegner)?.incomingCreeps ?? 0;
  }

  /** Zaehlt die Creeps, die gerade die eigene Lane angreifen. */
  private countOwnLaneCreeps(): number {
    const state = this.room?.state;
    const sessionId = this.room?.sessionId;
    if (!state?.creeps || !sessionId) return 0;

    let count = 0;
    for (const creep of state.creeps.values()) {
      if (creep.laneOwner === sessionId) count += 1;
    }
    return count;
  }

  private update(partial: Partial<MatchSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...partial };
    for (const listener of this.listeners) listener(this.snapshot);
  }
}

/** Serveradresse aus der Umgebungskonfiguration, mit sinnvollem Standardwert. */
export function resolveServerUrl(): string {
  const configured = import.meta.env.VITE_SERVER_URL;
  if (configured && configured.trim() !== '') return configured;

  // Fallback: derselbe Host wie die Webseite, Port 2567.
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.hostname}:2567`;
}

/**
 * Die Richtung, die tatsaechlich gesendet und vorhergesagt wird.
 *
 * Ein toter Hero bewegt sich nicht - und ein Hero mitten im Barrage auch
 * nicht. Der Server verwirft die Richtung in beiden Faellen. Sagte
 * der Client sie trotzdem vorher, zoege es die Figur im Bild in die
 * Laufrichtung, bis die naechste Serverantwort sie zurueckreisst. Genau das
 * war im Spiel zu sehen (25.09.2026): ein Ziehen vom Todesort weg und ein
 * Ruck zurueck.
 *
 * DIE EINE STELLE, an der Bildschirmrichtung zu Weltrichtung wird
 * (Produktentscheidung vom 08.10.2026).
 *
 * Beide Spieler sollen ihre eigene Seite unten links sehen. Erreicht wird
 * das ueber die Kamera: Sie steht fuer Blau um 180 Grad gedreht. Damit
 * zeigt derselbe Daumenausschlag fuer die beiden Seiten in
 * entgegengesetzte Weltrichtungen - und genau das wird hier ausgeglichen.
 *
 * Bei 180 Grad ist die Drehung einfach: beide Werte negieren.
 *
 * Warum an einer einzigen Stelle: Wer sie irgendwo vergisst, baut einen
 * Fehler, der sich nur im Spiel zeigt und in keinem Test auffaellt.
 *
 * HIER STAND BIS ZUM 13.10.2026, das Zielen von Skills brauche die Drehung
 * nicht - die Kamera rechne ohnehin um. Das stimmt nur fuer den einen von
 * zwei Wegen: Wer auf die Spielflaeche tippt, geht ueber screenToGround und
 * dreht mit. Der ZIELSTICK am Handy rechnet dagegen selbst aus der
 * Auslenkung, und der stand fest auf der Sicht von Orange. Im Spiel zeigten
 * Laufen und Zielen fuer Blau damit in entgegengesetzte Richtungen.
 *
 * Seitdem gibt es zwei solche Stellen, und beide sagen es: diese hier fuer
 * die Bewegung, resolveStickTarget fuer den Zielstick.
 *
 * SEIT DEM 18.10.2026 ZAEHLT AUCH DIE SCHRAEGE DER KAMERA
 * (Produktentscheidung, siehe bildschirmRichtung.ts): Nach oben heisst nach
 * oben im Bild, nicht mehr die Lane entlang. Die Eingabe kommt im Format
 * des InputControllers - x ist "nach unten", y ist "nach links" - und wird
 * hier in Bildschirmrichtung zurueck- und dann in die Spielrichtung
 * umgerechnet. Die 180-Grad-Drehung fuer Blau steckt dort mit drin.
 */
export function movementIntent(
  /** Darf sich der Hero gerade ueberhaupt bewegen? */
  alive: boolean,
  move: { x: number; y: number },
  /** Auf welcher Seite der gemeinsamen Lane der Spieler zuhause ist. */
  side: PlayerSide = 'orange',
): { x: number; y: number } {
  if (!alive) return { x: 0, y: 0 };
  return bildschirmZuSpiel(-move.y, -move.x, side);
}

/**
 * Der Rueckweg: eine Richtung IM SPIEL als Eingabe, wie sie der
 * InputController liefert (Produktentscheidung vom 25.10.2026).
 *
 * Genau die Umkehrung von movementIntent - und aus demselben Grund an
 * derselben Stelle: Wer eine der beiden aendert, sieht die andere.
 *
 * Gebraucht wird sie fuer die Maussteuerung. Ein angeklickter Punkt liegt
 * im Spiel, nicht auf dem Bildschirm; ohne diesen Rueckweg wuerde seine
 * Richtung unterwegs ein zweites Mal um die Kameraschraege gedreht, und
 * der Hero liefe im Bogen zum Ziel (im Spiel aufgefallen, Robin am
 * 25.10.2026: bis zu 6,35 Einheiten neben der Geraden).
 */
export function eingabeAusSpielrichtung(
  richtung: { x: number; y: number },
  side: PlayerSide = 'orange',
): { x: number; y: number } {
  const bild = spielZuBildschirm(richtung.x, richtung.y, side);
  // movementIntent liest x als "nach unten" und y als "nach links".
  return { x: -bild.oben, y: -bild.rechts };
}
